import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, Lightbulb, MessageCircleQuestion, MessageSquare, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { useOptionsMode } from '../../context/OptionsModeContext';
import { submitOptionsFeedback } from '../../lib/optionsApi';

const FEEDBACK_CATEGORIES = [
  { id: 'bug', icon: Bug, accentClassName: 'text-[#FF8A8A]', borderClassName: 'hover:border-[#FF8A8A]/35 hover:bg-[#FF8A8A]/6' },
  { id: 'suggestion', icon: Lightbulb, accentClassName: 'text-[#F0B90B]', borderClassName: 'hover:border-[#F0B90B]/35 hover:bg-[#F0B90B]/6' },
  { id: 'question', icon: MessageCircleQuestion, accentClassName: 'text-[#9BFFBE]', borderClassName: 'hover:border-[#00E676]/28 hover:bg-[#00E676]/6' },
];

export const OptionsFeedbackWidget = () => {
  const { t, language } = useOptionsLocale();
  const { isDemoMode } = useOptionsMode();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const categoryMap = useMemo(() => Object.fromEntries(FEEDBACK_CATEGORIES.map((item) => [item.id, item])), []);
  const selectedCategoryConfig = selectedCategory ? categoryMap[selectedCategory] : null;

  useEffect(() => {
    if (!open) {
      setSelectedCategory(null);
      setMessage('');
      setError('');
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  const placeholder = selectedCategory ? t(`optionsFeedback.placeholders.${selectedCategory}`) : '';

  const handleSubmit = async () => {
    if (!selectedCategory) return;
    if (message.trim().length < 3) {
      setError(t('optionsFeedback.errors.messageRequired'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await submitOptionsFeedback({
        category: selectedCategory,
        message: message.trim(),
        route: `${location.pathname}${location.search || ''}`,
        locale: language,
        view_mode: isDemoMode ? 'demo' : 'live',
      });
      setSuccess(true);
      toast.success(t('optionsFeedback.successToast'));
      window.setTimeout(() => setOpen(false), 1200);
    } catch {
      setError(t('optionsFeedback.errors.submitFailed'));
      toast.error(t('optionsFeedback.errors.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full border border-white/12 bg-[#11141D]/94 px-4 py-3 text-sm font-semibold text-white shadow-[0_18px_42px_rgba(0,0,0,0.34)] backdrop-blur transition-[background-color,border-color,transform] hover:-translate-y-0.5 hover:border-white/18 hover:bg-[#171B26]"
        data-testid="options-feedback-floating-button"
      >
        <MessageSquare size={16} />
        <span data-testid="options-feedback-floating-button-label">{t('optionsFeedback.button')}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[min(92vw,520px)] max-w-[520px] border-white/10 bg-[#07111f] p-0 text-white" data-testid="options-feedback-dialog">
          <div className="space-y-5 p-5 sm:p-6">
            <DialogHeader className="space-y-2 text-left">
              <DialogTitle className="text-xl font-black tracking-tight text-white" data-typography="heading" data-testid="options-feedback-dialog-title">{t('optionsFeedback.title')}</DialogTitle>
              <DialogDescription className="text-sm text-white/60" data-testid="options-feedback-dialog-description">{t('optionsFeedback.description')}</DialogDescription>
            </DialogHeader>

            {!selectedCategory && !success ? (
              <div className="space-y-3" data-testid="options-feedback-category-step">
                {FEEDBACK_CATEGORIES.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setSelectedCategory(item.id);
                        setError('');
                      }}
                      className={`flex w-full items-start gap-3 rounded-[20px] border border-white/10 bg-white/[0.03] px-4 py-4 text-left transition-[border-color,background-color] ${item.borderClassName}`}
                      data-testid={`options-feedback-category-${item.id}`}
                    >
                      <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-black/20 ${item.accentClassName}`}>
                        <Icon size={18} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-white" data-testid={`options-feedback-category-${item.id}-title`}>
                          {t(`optionsFeedback.categories.${item.id}.title`)}
                        </span>
                        <span className="mt-1 block text-sm text-white/58" data-testid={`options-feedback-category-${item.id}-description`}>
                          {t(`optionsFeedback.categories.${item.id}.description`)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}

            {selectedCategory && !success ? (
              <div className="space-y-4" data-testid="options-feedback-message-step">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategory(null);
                      setError('');
                    }}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white"
                    data-testid="options-feedback-back-button"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <div className="flex min-w-0 items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2" data-testid="options-feedback-selected-category-pill">
                    {selectedCategoryConfig ? <selectedCategoryConfig.icon size={15} className={selectedCategoryConfig.accentClassName} /> : null}
                    <span className="truncate text-sm font-semibold text-white">{t(`optionsFeedback.categories.${selectedCategory}.title`)}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-white" data-testid="options-feedback-textarea-label">{t('optionsFeedback.messageLabel')}</p>
                  <Textarea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder={placeholder}
                    rows={6}
                    disabled={submitting}
                    className="resize-none border-white/10 bg-black/20 text-white placeholder:text-white/30"
                    data-testid="options-feedback-message-textarea"
                  />
                </div>

                {error ? (
                  <div className="rounded-2xl border border-[#FF8A8A]/25 bg-[#FF8A8A]/10 px-4 py-3 text-sm text-[#FFD1D1]" data-testid="options-feedback-error">
                    {error}
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="rounded-full bg-[#F0B90B] px-5 text-black hover:bg-[#ffd26a] disabled:opacity-60"
                    data-testid="options-feedback-submit-button"
                  >
                    {submitting ? t('optionsFeedback.sending') : t('optionsFeedback.submit')}
                  </Button>
                </div>
              </div>
            ) : null}

            {success ? (
              <div className="space-y-3 rounded-[22px] border border-[#00E676]/20 bg-[#00E676]/10 px-4 py-5 text-center" data-testid="options-feedback-success-state">
                <p className="text-lg font-semibold text-white" data-testid="options-feedback-success-title">{t('optionsFeedback.successTitle')}</p>
                <p className="text-sm text-white/70" data-testid="options-feedback-success-description">{t('optionsFeedback.successDescription')}</p>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OptionsFeedbackWidget;